import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  Spinner,
  Form,
  Button,
  Row,
  Col,
  Badge,
  Pagination, 
} from "react-bootstrap";
import apiClient from "../../api/apiClient"; 
import { toast } from "react-toastify";

const AuctionDataOverview = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState([
    { id: Date.now(), field: "", operator: "=", value: "" }, 
  ]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
  });
  const [currentPage, setCurrentPage] = useState(1);

const filterableFields = [
  { label: "Item Name", value: "item_name", type: "text" },
  { label: "Is Draft", value: "is_draft", type: "boolean" },
  { label: "Item Sold", value: "item_sold", type: "boolean" },
  { label: "Auction Duration", value: "auctionDuration", type: "number" },
  { label: "Starting Bid", value: "starting_bid", type: "number" },
  { label: "Category (ID)", value: "category", type: "text" },
  { label: "Seller (ID)", value: "created_by", type: "text" },

  { label: "Slug", value: "slug", type: "text" },
  { label: "Sub-Category (ID)", value: "sub_category", type: "text" },
  { label: "Seller Type", value: "seller_type", type: "text" },
  { label: "Condition", value: "condition", type: "text" },
  { label: "Location", value: "location", type: "text" },
  { label: "Zipcode", value: "zipcode", type: "text" },
  { label: "Country Code", value: "country_code", type: "text" },
  { label: "Shipment Type", value: "shipmentType", type: "text" }, 
  { label: "Sold To (ID)", value: "sold_to", type: "text" },
  { label: "Latest Bidder (ID)", value: "latest_bid_by", type: "text" },
  { label: "Highest Bidder (ID)", value: "highestBidder", type: "text" },
  { label: "Relisted From (ID)", value: "relisted_from", type: "text" },
  { label: "Stripe Hold ID", value: "stripe_hold_id", type: "text" },

  { label: "Reserve Released", value: "reserve_released", type: "boolean" },
  { label: "Reserve Met", value: "reserveMet", type: "boolean" },
  { label: "Admin Approval", value: "admin_approval", type: "boolean" },
  { label: "Withdrawn by Admin", value: "withdrawnByAdmin", type: "boolean" },
  { label: "Is Featured", value: "is_featured", type: "boolean" },
  { label: "Time Up", value: "time_up", type: "boolean" },
  { label: "15m Warning Sent", value: "fifteenMinuteWarningNotified", type: "boolean" },
  { label: "1h Warning Sent", value: "oneHourWarningNotified", type: "boolean" },

  { label: "Reserve Price", value: "reserver_price", type: "number" },
  { label: "Bid Price", value: "bid_price", type: "number" },
  { label: "Sold Price", value: "sold_price", type: "number" },
  { label: "Bid Count", value: "bidCount", type: "number" },
  { label: "Relist Count", value: "relist_count", type: "number" },

  { label: "Edited By", value: "edited_by", type: "text" }, 
  { label: "Post Auction Status", value: "postAuctionStatus", type: "text" },
];

  const fetchAuctionData = async (currentFilters, page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      const activeFilters = currentFilters.filter(
        (f) => f.field && f.value !== ""
      );
      if (activeFilters.length > 0) {
        params.append("filters", JSON.stringify(activeFilters));
      }

      params.append("page", page.toString());
      params.append("limit", "20"); 

      const res = await apiClient.get(
        `/data/auctions?${params.toString()}`
      );

      if (res.data && res.data.status) {
        setAuctions(res.data.data);
        setPagination(res.data.pagination); 
        setCurrentPage(res.data.pagination.currentPage);
      } else {
        toast.error("Failed to fetch auction data.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuctionData(filters, 1);
  }, []); 

  const updateFilter = (id, newFilterData) => {
    setFilters((currentFilters) => {
      let justAddedNewRow = false;
      const lastFilter = currentFilters[currentFilters.length - 1];

      const updatedFilters = currentFilters.map((f) =>
        f.id === id ? { ...f, ...newFilterData } : f
      );

      if (
        lastFilter.id === id &&
        !newFilterData.field && 
        lastFilter.field !== "" &&
        newFilterData.value !== ""
      ) {
        updatedFilters.push({
          id: Date.now(),
          field: "",
          operator: "=",
          value: "",
        });
        justAddedNewRow = true;
      }

      if (
        lastFilter.id === id &&
        newFilterData.field &&
        lastFilter.value !== "" &&
        !justAddedNewRow
      ) {
        updatedFilters.push({
          id: Date.now(),
          field: "",
          operator: "=",
          value: "",
        });
      }

      return updatedFilters;
    });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchAuctionData(filters, 1);
  };

  const handlePageChange = (pageNumber) => {
    fetchAuctionData(filters, pageNumber);
  };

  const UserLink = ({ user }) => {
    if (!user) return <Badge bg="secondary">N/A</Badge>;
    return (
      <Link
        to={`/usersdata?userId=${user._id}`}
        className="d-flex align-items-center gap-2"
        style={{ textDecoration: 'none' }}
      >
        <img
          src={user.profile_pic}
          alt={user.username}
          style={{
            width: 25,
            height: 25,
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
        {user.username || user.email}
      </Link>
    );
  };

  return (
    <div className="container-fluid p-4">
      <h2 className="mb-4">Auction Data Overview</h2>

      <Form onSubmit={handleFilterSubmit} className="mb-4 p-3 bg-light rounded">
        <h4>Advanced Filters</h4>
        {filters.map((filter) => {
          const fieldConfig = filterableFields.find(
            (f) => f.value === filter.field
          );

          return (
            <Row key={filter.id} className="g-3 mb-2 align-items-center">
              <Col md={4}>
                <Form.Select
                  name="field"
                  value={filter.field}
                  onChange={(e) =>
                    updateFilter(filter.id, {
                      field: e.target.value,
                      value: "", 
                    })
                  }
                >
                  <option value="">Select a field...</option>
                  {filterableFields.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={2}>
                <Form.Select name="operator" disabled>
                  <option value="=">equals</option>
                  {/* You could add more: $gt, $lt, $regex etc. */}
                </Form.Select>
              </Col>

              <Col md={6}>
                {fieldConfig?.type === "boolean" ? (
                  <Form.Select
                    name="value"
                    value={filter.value}
                    onChange={(e) =>
                      updateFilter(filter.id, { value: e.target.value })
                    }
                    disabled={!filter.field}
                  >
                    <option value="">Select...</option>
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </Form.Select>
                ) : (
                  <Form.Control
                    type={fieldConfig?.type === "number" ? "number" : "text"}
                    name="value"
                    placeholder="Enter value..."
                    value={filter.value}
                    onChange={(e) =>
                      updateFilter(filter.id, { value: e.target.value })
                    }
                    disabled={!filter.field} 
                  />
                )}
              </Col>
            </Row>
          );
        })}
        <Button type="submit" variant="primary" className="mt-2">
          Search
        </Button>
      </Form>

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p>Loading auction data...</p>
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Status</th>
                <th>Seller</th>
                <th>Category</th>
                <th>Bids</th>
                <th>Watchers</th>
                <th>Sold To</th>
                <th>End Date</th>
              </tr>
            </thead>
            <tbody>
              {auctions.map((item) => (
                <tr key={item._id}>
                  <td>
                    <Link to={`/auctionsdata/${item._id}`}>
                      <strong>{item.item_name}</strong>
                    </Link>
                    <br />
                    <small className="text-muted">{item._id}</small>
                  </td>
                  <td>
                    {item.is_draft ? (
                      <Badge bg="warning">Draft</Badge>
                    ) : item.item_sold ? (
                      <Badge bg="success">Sold</Badge>
                    ) : new Date(item.end_date) > new Date() ? (
                      <Badge bg="info">Live</Badge>
                    ) : (
                      <Badge bg="secondary">Ended</Badge>
                    )}
                  </td>
                  <td>
                    <UserLink user={item.created_by} />
                  </td>
                  <td>{item.category?.name || "N/A"}</td>
                  <td>{item.bids?.length || 0}</td>
                  <td>{item.watchers?.length || 0}</td>
                  <td>
                    <UserLink user={item.sold_to} />
                  </td>
                  <td>
                    {item.end_date
                      ? new Date(item.end_date).toLocaleString()
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex justify-content-center">
            <Pagination>
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              <Pagination.Item active>
                Page {currentPage} of {pagination.totalPages}
              </Pagination.Item>

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={currentPage === pagination.totalPages}
              />
            </Pagination>
          </div>
        </>
      )}
      {auctions.length === 0 && !loading && (
        <p className="text-center">No auctions found matching these filters.</p>
      )}
    </div>
  );
};

export default AuctionDataOverview;